/**
 * @voltxampere/engine — Backward Euler companion models (SPECS §3.8)
 *
 * BE is a first-order implicit method. It is **L-stable** (damps any
 * stiff transient regardless of step size) which makes it the safe
 * fallback when TRAP / Gear-2 fail or when no history is available
 * yet (Gear-2's first two steps borrow BE because it lacks the
 * `x(t-2h)` it needs).
 *
 * Capacitor:
 *
 *   i_C(t) = (C/h) · (V_C(t) − V_C(t−h))
 *          = Geq · V_C(t) + Ieq
 *   Geq    = C/h
 *   Ieq    = −Geq · V_C(t−h)
 *
 * Inductor (dual: swap V↔I, C↔L):
 *
 *   V_L(t) = (L/h) · (I_L(t) − I_L(t−h))
 *          = Geq · I_L(t) + Ieq
 *   Geq    = L/h
 *   Ieq    = −Geq · I_L(t−h)
 *
 * Both functions throw on non-finite or non-positive `h` / `C` /
 * `L`. The solver guarantees positive `h` (clamped to `h_min`
 * before the call), so a throw here means a planner / context bug.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
/**
 * Capacitor companion under Backward Euler.
 *
 * @param C        capacitance in farads (must be > 0).
 * @param h        step size in seconds (must be > 0).
 * @param VCPrev   capacitor voltage at the previous accepted step.
 */
export function capacitorBE(C, h, VCPrev) {
    if (!(C > 0) || !Number.isFinite(C)) {
        throw new ContractViolationError(`capacitorBE: C must be a positive finite number, got ${String(C)}`);
    }
    if (!(h > 0) || !Number.isFinite(h)) {
        throw new ContractViolationError(`capacitorBE: h must be a positive finite number, got ${String(h)}`);
    }
    if (!Number.isFinite(VCPrev)) {
        throw new ContractViolationError(`capacitorBE: VCPrev must be finite, got ${String(VCPrev)}`);
    }
    const Geq = C / h;
    const Ieq = -Geq * VCPrev;
    return { Geq, Ieq };
}
/**
 * Inductor companion under Backward Euler.
 *
 * @param L        inductance in henries (must be > 0).
 * @param h        step size in seconds (must be > 0).
 * @param ILPrev   inductor branch current at the previous accepted
 *                 step (positive direction: pos → neg, matching the
 *                 augmented-row sign convention).
 */
export function inductorBE(L, h, ILPrev) {
    if (!(L > 0) || !Number.isFinite(L)) {
        throw new ContractViolationError(`inductorBE: L must be a positive finite number, got ${String(L)}`);
    }
    if (!(h > 0) || !Number.isFinite(h)) {
        throw new ContractViolationError(`inductorBE: h must be a positive finite number, got ${String(h)}`);
    }
    if (!Number.isFinite(ILPrev)) {
        throw new ContractViolationError(`inductorBE: ILPrev must be finite, got ${String(ILPrev)}`);
    }
    const Geq = L / h;
    const Ieq = -Geq * ILPrev;
    return { Geq, Ieq };
}
//# sourceMappingURL=backward-euler.js.map